import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Brain, Calendar, Package, 
  Sparkles, RefreshCw, AlertCircle, BarChart3, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

import { API_URL } from '@/config/api';

export default function AdminDemandForecasting() {
  const { token } = useAuth();
  const [forecast, setForecast] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    fetchForecast();
  }, [token]);

  const fetchForecast = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/forecasting/predict?days_ahead=7`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (response.ok) {
        setForecast(await response.json());
      }
    } catch (error) {
      console.error('Error fetching forecast:', error);
      toast.error('Failed to load forecast');
    } finally {
      setLoading(false);
    }
  };

  const fetchAIInsights = async () => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/forecasting/ai-insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAiInsights(data);
        toast.success('AI analysis complete');
      } else {
        toast.error('Failed to generate AI insights');
      }
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      toast.error('Failed to generate AI insights');
    } finally {
      setAiLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="h-5 w-5 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="h-5 w-5 text-red-500" />;
    return <span className="h-5 w-5 text-slate-400">→</span>;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === 'high') return 'bg-green-100 text-green-700';
    if (confidence === 'medium') return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const dayColors = {
    Monday: 'bg-blue-50',
    Tuesday: 'bg-green-50',
    Wednesday: 'bg-purple-50',
    Thursday: 'bg-amber-50',
    Friday: 'bg-red-50',
    Saturday: 'bg-pink-50',
    Sunday: 'bg-indigo-50'
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-100 rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-32" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-demand-forecasting">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-500" />
            Demand Forecasting
          </h1>
          <p className="text-slate-500 mt-1">AI-powered predictions to help you plan ahead</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchForecast}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={fetchAIInsights} 
            disabled={aiLoading}
            className="bg-purple-500 hover:bg-purple-600"
          >
            {aiLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Get AI Insights
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {forecast?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Daily Items</p>
                  <p className="text-2xl font-bold">{forecast.summary.avg_daily_items}</p>
                </div>
                <Package className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Trend</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">
                      {forecast.summary.trend_percentage > 0 ? '+' : ''}
                      {forecast.summary.trend_percentage}%
                    </p>
                    {getTrendIcon(forecast.summary.trend_direction)}
                  </div>
                </div>
                <BarChart3 className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Data Period</p>
                  <p className="text-lg font-medium">{forecast.data_period}</p>
                </div>
                <Calendar className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="predictions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="predictions">7-Day Forecast</TabsTrigger>
          <TabsTrigger value="patterns">Day Patterns</TabsTrigger>
          <TabsTrigger value="items">Top Items Forecast</TabsTrigger>
          {aiInsights && <TabsTrigger value="ai">AI Insights</TabsTrigger>}
        </TabsList>

        {/* Daily Predictions */}
        <TabsContent value="predictions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                Predicted Order Volume (Next 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-3">
                {forecast?.daily_predictions?.map((prediction, idx) => (
                  <motion.div
                    key={prediction.date}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className={`${dayColors[prediction.day] || 'bg-slate-50'} rounded-xl p-4 text-center`}
                  >
                    <p className="text-sm font-medium text-slate-600">{prediction.day}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(prediction.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                    </p>
                    <p className="text-3xl font-bold text-slate-900 my-2">
                      {prediction.predicted_items}
                    </p>
                    <p className="text-xs text-slate-500">items predicted</p>
                    <Badge className={`mt-2 ${getConfidenceColor(prediction.confidence)}`}>
                      {prediction.confidence}
                    </Badge>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Day of Week Patterns */}
        <TabsContent value="patterns">
          <Card>
            <CardHeader>
              <CardTitle>Day of Week Patterns</CardTitle>
              <CardDescription>Average items sold by day of the week</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {forecast?.day_of_week_patterns && Object.entries(forecast.day_of_week_patterns)
                  .sort(([, a], [, b]) => b - a)
                  .map(([day, avg], idx) => {
                    const maxAvg = Math.max(...Object.values(forecast.day_of_week_patterns));
                    const percentage = (avg / maxAvg) * 100;
                    
                    return (
                      <div key={day} className="flex items-center gap-4">
                        <span className="w-24 font-medium">{day}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                            className={`h-full ${idx === 0 ? 'bg-green-500' : 'bg-primary'}`}
                          />
                        </div>
                        <span className="w-16 text-right font-bold">{avg.toFixed(0)}</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Items Forecast */}
        <TabsContent value="items">
          <Card>
            <CardHeader>
              <CardTitle>Top Items Weekly Forecast</CardTitle>
              <CardDescription>Predicted demand for your most popular items</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {forecast?.top_items_forecast?.map((item, idx) => (
                  <motion.div
                    key={item.item_name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg"
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'
                    }`}>
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">{item.item_name}</p>
                      <p className="text-sm text-slate-500">
                        ~{item.avg_daily_quantity} per day
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-primary">{item.predicted_weekly}</p>
                      <p className="text-xs text-slate-500">predicted this week</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights */}
        {aiInsights && (
          <TabsContent value="ai">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  AI-Powered Business Insights
                </CardTitle>
                <CardDescription>
                  Generated: {new Date(aiInsights.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {aiInsights.data_summary && (
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold">{aiInsights.data_summary.total_orders}</p>
                      <p className="text-sm text-slate-500">Orders Analyzed</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">
                        R{aiInsights.data_summary.total_revenue?.toLocaleString()}
                      </p>
                      <p className="text-sm text-slate-500">Total Revenue</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        R{aiInsights.data_summary.avg_order_value}
                      </p>
                      <p className="text-sm text-slate-500">Avg Order Value</p>
                    </div>
                  </div>
                )}
                
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap font-sans text-slate-700 bg-transparent p-0">
                      {aiInsights.ai_insights}
                    </pre>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Recommendation Banner */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Planning Recommendation</p>
              <p className="text-sm text-amber-700">
                Based on your trends, consider increasing prep capacity by {Math.abs(forecast?.summary?.trend_percentage || 0).toFixed(0)}% 
                for the upcoming week. Focus on your top sellers and ensure adequate stock levels.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
